import { Component, Injectable, computed, inject, input, signal } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { catchError, filter, throwError } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SubscriptionStatusResponse, planIncludes } from './subscription.service';
import { MANAGED_WRITE_FEATURES, managedFeatureForRoute } from '../../../../../../shared-util/src/lib/plan-feature-policy';

@Injectable({ providedIn: 'root' })
export class PlanFeatureFeedback {
  readonly refusal = signal<{ orgId: string; unavailable: boolean; message: string } | null>(null);
  show(error: unknown, url: string): void {
    if (!(error instanceof HttpErrorResponse)) return;
    const code = String(error.error?.errorCode ?? error.error?.code ?? '');
    const unavailable = error.status === 503 && ['223', 'PLAN_CHECK_FAILED'].includes(code);
    const denied = error.status === 403 && ['222', 'FEATURE_NOT_IN_PLAN'].includes(code);
    const orgId = /\/organizations?\/([a-f0-9-]{36})(?:\/|$)/i.exec(url)?.[1];
    if (!orgId || (!unavailable && !denied)) return;
    this.refusal.set({ orgId, unavailable, message: unavailable
      ? 'We could not verify your plan access. Your changes have not been submitted. Please try the action again.'
      : typeof error.error?.message === 'string' ? error.error.message : 'This action is not included in your workspace plan.' });
  }
}

/** Preserve the original error for the form; do not replay writes or clear entered data. */
export const planFeatureFeedbackInterceptor: HttpInterceptorFn = (request, next) => {
  const feedback = inject(PlanFeatureFeedback);
  return next(request).pipe(catchError(error => {
    feedback.show(error, request.url);
    return throwError(() => error);
  }));
};

@Component({
  selector: 'app-plan-feature-feedback', standalone: true, imports: [RouterLink],
  template: `@if (notice(); as item) {
    <section class="plan-notice" role="status" aria-live="polite">
      <div><strong>{{ item.unavailable ? 'Plan access unavailable' : 'Plan access' }}</strong><p>{{ item.message }}</p></div>
      @if (!item.unavailable) { <a routerLink="/settings" [queryParams]="{orgId: organizationId(), section: 'subscriptionBilling'}">Review plan</a> }
      @if (isRefusal()) { <button type="button" aria-label="Dismiss plan notice" (click)="feedback.refusal.set(null)">Dismiss</button> }
    </section>
  }`,
  styles: [`.plan-notice{display:flex;align-items:center;gap:1rem;flex-wrap:wrap;padding:1rem 1.25rem;margin-bottom:1rem;border:1px solid var(--border-color,#d9e2ef);border-radius:.75rem;background:var(--surface-card,#fff);color:var(--text-color,#172033)}
    .plan-notice div{flex:1;min-width:12rem}.plan-notice p{margin:.35rem 0 0}.plan-notice a{color:var(--primary-color,#087c9c)}
    .plan-notice button{background:transparent;color:inherit;border:1px solid var(--border-color,#d9e2ef);border-radius:.5rem;padding:.5rem .75rem;cursor:pointer}
    :host-context([data-theme='dark']) .plan-notice{background:var(--surface-card,#101b2b);color:var(--text-color,#e7edf5);border-color:var(--border-color,#334155)}`],
})
export class PlanFeatureFeedbackBanner {
  readonly organizationId = input<string | null>(null);
  readonly summary = input<SubscriptionStatusResponse | null>(null);
  readonly feedback = inject(PlanFeatureFeedback);
  private readonly router = inject(Router);
  private readonly navigation = toSignal(this.router.events.pipe(filter(event => event instanceof NavigationEnd)));
  readonly isRefusal = computed(() => this.feedback.refusal()?.orgId === this.organizationId());
  readonly notice = computed(() => {
    this.navigation();
    const orgId = this.organizationId();
    if (!orgId) return null;
    const refusal = this.feedback.refusal();
    if (refusal?.orgId === orgId) return refusal;
    const feature = managedFeatureForRoute(this.router.url), summary = this.summary();
    if (!feature || summary?.featureEnforcement !== true || planIncludes(summary, feature)) return null;
    return { unavailable: false, message: `${MANAGED_WRITE_FEATURES[feature]} is not included in this plan. Existing records remain available; creating or changing records requires a plan that includes this feature.` };
  });
}
